import React, { useEffect, useId, useRef, useState } from 'react';

import { Check, ChevronDown, X } from 'lucide-react';

import {
  type InteractiveSize,
  chipSizes,
  getIconPixels,
  interactiveSizes,
} from '@/theme/size-tokens';

export type MultiSelectOption = {
  value: string;
  label: string;
};

export type MultiSelectProps = {
  /**
   * Available options to select from
   */
  options: MultiSelectOption[];
  /**
   * Currently selected values
   */
  value?: string[];
  /**
   * Callback when selection changes
   */
  onChange?: (values: string[]) => void;
  /**
   * Placeholder text when no selection
   */
  placeholder?: string;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Maximum number of selections allowed
   */
  maxSelections?: number;
  /**
   * Size variant - uses unified size system
   */
  size?: InteractiveSize;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Text to display when no options are found
   */
  noneFoundText?: string;
};

/**
 * MultiSelect - Select multiple options from a dropdown list
 *
 * The search input lives inside the trigger alongside selected chips —
 * no separate search row in the dropdown.
 *
 * @example
 * ```tsx
 * const [selected, setSelected] = useState<string[]>([]);
 *
 * <MultiSelect
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' }
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 *   placeholder="Select options..."
 * />
 * ```
 */
function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  noneFoundText = 'No options found',
  disabled = false,
  maxSelections,
  size = 'md',
  className = '',
}: Readonly<MultiSelectProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValues = value || [];

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleOption = (optionValue: string) => {
    const isSelected = selectedValues.includes(optionValue);
    let newValues: string[];

    if (isSelected) {
      newValues = selectedValues.filter((v) => v !== optionValue);
    } else {
      if (maxSelections && selectedValues.length >= maxSelections) {
        return;
      }
      newValues = [...selectedValues, optionValue];
    }

    onChange?.(newValues);
    setSearchQuery('');
  };

  const handleRemoveValue = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = selectedValues.filter((v) => v !== valueToRemove);
    onChange?.(newValues);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.([]);
    setSearchQuery('');
  };

  const listboxId = useId();
  const optionDomId = (index: number) => `${listboxId}-option-${index}`;

  const handleTriggerClick = () => {
    if (disabled) return;
    // Toggle, so a click on the control chrome or the chevron can close an open
    // dropdown (the search input stops propagation, so typing there never closes).
    setIsOpen((prev) => !prev);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleToggleOption(filteredOptions[focusedIndex].value);
          setFocusedIndex(-1);
        }
        break;
      case 'Backspace':
        if (searchQuery === '' && selectedValues.length > 0) {
          const newValues = selectedValues.slice(0, -1);
          onChange?.(newValues);
        }
        break;
    }
  };

  const sizeClasses = {
    xs: {
      container:
        interactiveSizes.xs.height.split(' ')[0].replace('h-', 'min-h-') +
        ' py-0.5 px-1.5',
      text: interactiveSizes.xs.text,
      chip: chipSizes.sm.classes
        .split(' ')
        .filter((c) => !c.includes('gap'))
        .join(' '),
      icon: getIconPixels('xs', 'desktop'),
    },
    sm: {
      container:
        interactiveSizes.sm.height.split(' ')[0].replace('h-', 'min-h-') +
        ' py-1 px-2',
      text: interactiveSizes.sm.text,
      chip: chipSizes.sm.classes
        .split(' ')
        .filter((c) => !c.includes('gap'))
        .join(' '),
      icon: getIconPixels('sm', 'desktop'),
    },
    md: {
      container:
        interactiveSizes.md.height.split(' ')[0].replace('h-', 'min-h-') +
        ' py-2 px-3',
      text: interactiveSizes.md.text,
      chip: chipSizes.md.classes
        .split(' ')
        .filter((c) => !c.includes('gap'))
        .join(' '),
      icon: getIconPixels('md', 'desktop'),
    },
    lg: {
      container:
        interactiveSizes.lg.height.split(' ')[0].replace('h-', 'min-h-') +
        ' py-2 px-4',
      text: interactiveSizes.lg.text,
      chip: chipSizes.lg.classes
        .split(' ')
        .filter((c) => !c.includes('gap'))
        .join(' '),
      icon: getIconPixels('lg', 'desktop'),
    },
    xl: {
      container:
        interactiveSizes.xl.height.split(' ')[0].replace('h-', 'min-h-') +
        ' py-2.5 px-5',
      text: interactiveSizes.xl.text,
      chip: chipSizes.lg.classes
        .split(' ')
        .filter((c) => !c.includes('gap'))
        .join(' '),
      icon: getIconPixels('xl', 'desktop'),
    },
  };

  const currentSize = sizeClasses[size];

  const getSelectedLabels = () => {
    return selectedValues
      .map((v) => options.find((opt) => opt.value === v)?.label)
      .filter(Boolean);
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Trigger — chips + inline search input */}
      <div
        className={`border-input bg-background text-foreground flex w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border transition-all duration-200 ${currentSize.container} ${currentSize.text} ${isOpen ? 'ring-ring ring-2 ring-offset-2' : 'hover:border-ring'} ${disabled ? 'cursor-not-allowed opacity-50' : ''} `.trim()}
        onClick={handleTriggerClick}
      >
        {/* Selected items as chips */}
        {getSelectedLabels().map((label, index) => (
          <span
            key={selectedValues[index]}
            className={`bg-primary/10 text-primary animate-scaleIn inline-flex origin-left items-center gap-1 rounded-md font-medium transition-all duration-200 ${currentSize.chip} `.trim()}
          >
            {label}
            <button
              type="button"
              onClick={(e) => handleRemoveValue(selectedValues[index], e)}
              className="hover:bg-primary/20 rounded-sm transition-colors duration-150"
              aria-label={`Remove ${label}`}
              tabIndex={-1}
            >
              <X size={getIconPixels(size, 'desktop')} />
            </button>
          </span>
        ))}

        {/* Inline search input — always visible when open, placeholder when closed */}
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setFocusedIndex(-1);
            if (!isOpen) setIsOpen(true);
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          disabled={disabled}
          className={`text-foreground placeholder:text-muted-foreground min-w-[6rem] flex-1 bg-transparent outline-none ${currentSize.text}`}
          aria-label="Search options"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            isOpen && focusedIndex >= 0 ? optionDomId(focusedIndex) : undefined
          }
        />

        {/* Right side icons */}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {selectedValues.length > 0 && !disabled && (
            <button
              type="button"
              onClick={handleClearAll}
              className="hover:bg-muted rounded p-1 transition-colors"
              aria-label="Clear all"
              tabIndex={-1}
            >
              <X size={getIconPixels(size, 'desktop')} />
            </button>
          )}
          <ChevronDown
            size={getIconPixels(size, 'desktop')}
            className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown — options only, no separate search row */}
      {isOpen && !disabled && (
        <div className="bg-card border-border animate-scaleIn absolute top-full right-0 left-0 z-50 mt-1 max-h-56 origin-top overflow-y-auto rounded-md border shadow-lg">
          <div role="listbox" id={listboxId} aria-label={placeholder}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = selectedValues.includes(option.value);
                const isFocused = index === focusedIndex;
                const isDisabled = Boolean(
                  maxSelections &&
                  !isSelected &&
                  selectedValues.length >= maxSelections
                );

                return (
                  <button
                    key={option.value}
                    id={optionDomId(index)}
                    type="button"
                    onClick={() =>
                      !isDisabled && handleToggleOption(option.value)
                    }
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-all duration-200 ${currentSize.text} ${isFocused ? 'bg-muted' : ''} ${isSelected ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'} ${isDisabled ? 'cursor-not-allowed opacity-50' : ''} `.trim()}
                    role="option"
                    aria-selected={isSelected}
                    disabled={isDisabled}
                  >
                    <span className="flex-1 truncate">{option.label}</span>
                    {isSelected && (
                      <Check
                        size={getIconPixels(size, 'desktop')}
                        className="animate-scaleIn shrink-0"
                      />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="text-muted-foreground px-3 py-6 text-center">
                {noneFoundText}
              </div>
            )}
          </div>

          {maxSelections && (
            <div className="border-border bg-muted/30 text-muted-foreground border-t px-3 py-2 text-xs">
              {selectedValues.length} / {maxSelections} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
